import { Stack, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import Button from '../Buttons/Button/Button';
import InnerLayout from '../Core/Layouts/InnerLayout/InnerLayout';

/**
 * Represents the ErrorPage component.
 *
 * @returns {JSX.Element} The rendered ErrorPage component.
 */
function ErrorPage({ error }: { error?: Error }) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).error;

    const reload = async (): Promise<void> => {
        const client = localStorage.getItem('client');
        const cookies = localStorage.getItem('cookies');
        localStorage.clear();
        if (client) {
            localStorage.setItem('client', client);
        }
        cookies && localStorage.setItem('cookies', cookies);
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        }
        location.reload();
    };

    const copyErrorToClipboard = async (): Promise<void> => {
        if (!error) return;

        const errorText = `Error Name: ${error.name}\nError Message: ${error.message}\n${error.stack ? `Stack Trace:\n${error.stack}` : ''}`;

        try {
            await navigator.clipboard.writeText(errorText);
        } catch (err) {
            console.error('Failed to copy error to clipboard:', err);
        }
    };

    return (
        <InnerLayout title={langStrings.unknownErrorTitle}>
            <Typography variant="body1">
                {langStrings.unknownErrorText}
                <br />
                {error && (
                    <div>
                        <Typography variant="body2">
                            {langStrings.errorName}: {error.name}
                        </Typography>
                        <Typography variant="body2">
                            {langStrings.errorMessage}: {error.message}
                        </Typography>
                        {error.stack && <Typography variant="body2">{langStrings.stackTrace}:</Typography>}
                        {error.stack && (
                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                {error.stack}
                            </Typography>
                        )}
                    </div>
                )}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button text={'Cache leeren & Seite Neuladen'} onClick={reload} />
                {error && <Button text={'Fehler kopieren'} onClick={copyErrorToClipboard} />}
            </Stack>
        </InnerLayout>
    );
}

export default ErrorPage;
